<template>
  <div class="overview">
    <section class="stats columns has-text-centered">
      <div class="column">
        <article class="card">
          <div class="card-content">
            <p class="title">
              {{ stats.openProjects }}
              <b-icon v-if="stats.openProjects === null"
                      icon="loading"
                      custom-class="mdi-spin" />
            </p>

            <p class="subtitle">Åbne projekter</p>
          </div>
        </article>
      </div>

      <div class="column">
        <article class="card">
          <div class="card-content">
            <p class="title">
              {{ stats.acceptedProjects }}
              <b-icon v-if="stats.acceptedProjects === null"
                      icon="loading"
                      custom-class="mdi-spin" />
            </p>

            <p class="subtitle">Accepterede projekter</p>
          </div>
        </article>
      </div>

      <div class="column">
        <article class="card">
          <div class="card-content">
            <p class="title"
               v-if="stats.projectValue !== null">
              {{ stats.projectValue }} kr.
            </p>
            <p class="title"
               v-else>
              <b-icon icon="loading"
                      custom-class="mdi-spin" />
            </p>

            <p class="subtitle">Projekt-værdi</p>
          </div>
        </article>
      </div>

      <div class="column">
        <article class="card">
          <div class="card-content">
            <p class="title">
              {{ stats.completedProjects }}
              <b-icon v-if="stats.completedProjects === null"
                      icon="loading"
                      custom-class="mdi-spin" />
            </p>

            <p class="subtitle">Afsluttede projekter</p>
          </div>
        </article>
      </div>
    </section>

    <section class="columns">
      <div class="column">
        <div class="card">
          <header class="card-header">
            <p class="card-header-title">Alle Projekter</p>
          </header>

          <div class="card-content">
            <b-table @sort="handleSort"
                     :loading="loading"
                     :data="projects"
                     hoverable
                     paginated
                     pagination-simple
                     backend-pagination
                     :default-sort="[sortColumn, sortOrder]"
                     :current-page.sync="page"
                     :total="total"
                     :per-page="limit">
              <template slot-scope="props">
                <b-table-column field="name"
                                label="Navn"
                                sortable>
                  <span @contextmenu="handler($event, props.row)">{{ props.row.name }}</span>
                </b-table-column>

                <b-table-column field="status"
                                label="Status"
                                sortable>
                  {{ $t(`projects.status.${props.row.status}`) }}
                </b-table-column>

                <b-table-column field="customer.name"
                                label="Kunde"
                                sortable
                                searchable>
                  <router-link class="button is-small"
                               :to="{
                                 name: 'administration.customers.detail',
                                 params: { id: props.row.customer.id }
                               }">
                    <span>{{ props.row.customer.name }}</span>
                  </router-link>
                </b-table-column>

                <b-table-column field="student.name"
                                label="Studerende"
                                searchable
                                sortable>
                  <router-link class="button is-small"
                               :to="{
                                 name: 'administration.students.detail',
                                 params: { id: props.row.student.id }
                               }">
                    <b-icon icon="information"
                            size="is-small" />
                    <span>{{ props.row.student.name }}</span>
                  </router-link>
                </b-table-column>

                <b-table-column field="skill.name"
                                label="Færdighed"
                                sortable>
                  {{ $t(`skills.${props.row.skill.name}.name`) }}
                </b-table-column>

                <b-table-column v-if="dateColumnSource === 'created_at'"
                                field="created_at"
                                label="Oprettet"
                                sortable
                                centered>
                  <span class="tag is-success">
                    {{
                      new Date(props.row.created_at).toISOString().split('T')[0]
                    }}
                  </span>
                </b-table-column>

                <b-table-column v-else
                                field="updated_at"
                                label="Afsluttet"
                                sortable
                                centered>
                  <span class="tag is-success">
                    {{
                      new Date(props.row.updated_at).toISOString().split('T')[0]
                    }}
                  </span>
                </b-table-column>
                <b-table-column>
                  <button type="button"
                          class="button is-small is-info"
                          @click="showMessageModalForProject(props.row)">
                    Se samtale
                  </button>
                </b-table-column>
              </template>
            </b-table>
          </div>
        </div>

        <context-menu @closeproject="closeProject"
                      @showprojectnotes="showNotesModalForProject"
                      :display="showContextMenu"
                      :lastActionDays="lastActionDays"
                      :creationDays="creationDays"
                      :currentStatus="currentStatus"
                      ref="menu">
        </context-menu>


      </div>
    </section>

    <project-conversation-modal v-if="isShowingMessagesModal"
                                :project="conversationForMessagesModal"
                                @close="hideModal" />

    <project-notes-modal v-if="isShowingProjectNotesModal"
                         :project="project"
                         @close="isShowingProjectNotesModal = false" 
                         @closeAndUpdate="updateProjectNotes" 
                         />
  </div>
</template>


<script>
import ProjectConversationModal from '@/taskflex/administration/components/projects/ConversationModal'
import ProjectNotesModal from '../../components/projects/ProjectNotesModal.vue';
import ProjectService from '@/taskflex/projects/services/ProjectService'

import ContextMenu from '@/taskflex/administration/components/projects/ProjectContextMenu.vue';
import CContextMenu from '@/taskflex/administration/components/projects/ConversationsContextMenu.vue';

export default {
  components: {
    ProjectConversationModal,
    ProjectNotesModal,
    ContextMenu,
    CContextMenu
  },
  data() {
    return {
      project: null,
      lastActionDays: 0,
      creationDays: 0,
      currentStatus: '',

      showContextMenu: false,
      projects: [],
      loading: false,
      total: 0,
      page: 1,
      limit: 999,
      stats: {
        completedProjects: null,
        acceptedProjects: null,
        openProjects: null,
        projectValue: null
      },

      isShowingProjectNotesModal: false,
      isShowingMessagesModal: false,
      conversationForMessagesModal: false,

      notesForProject: null

    }
  },

  computed: {
    sortOrder() {
      // If sortOrder key found in sessionStorage return this value else return 'desc'
      return sessionStorage.getItem('sortOrder') || 'desc';
    },
    sortColumn() {
      // If sortField key found in sessionStorage return this value else return 'created_at'
      return sessionStorage.getItem('sortField') || this.dateColumnSource;
    },
    filter() {
      const { filter = null } = this.$route.meta

      return filter
    },
    dateColumnSource() {
      return {
        open: 'created_at',
        'project-created': 'created_at',
        'project-clarification': 'created_at',
        'project-offer-accepted': 'created_at',
        'project-finalized': 'updated_at',
        'student-closed-project': 'updated_at',
        'project-offer-posted': 'created_at',
        'customer-closed-project': 'updated_at',
        'employee-closed-project': 'updated_at',
      }[this.filter]
    }
  },
  methods: {
    calculateDaysSince(date) {
      const now = new Date();
      const timeDiff = Math.abs(now.getTime() - date.getTime());
      return Math.ceil(timeDiff / (1000 * 3600 * 24));
    },
    projectIsOpen(status) {
      // if project status is found in array return true otherwise return false
      return [
        'project-created',
        'project-clarification',
        'project-accepted',
        'project-offer-posted',
        'project-offer-accepted'
      ].includes(status);
    },
    async closeProject() {
      const message = prompt('Angiv årsag til projektafslutning');
      const conf = confirm(`${this.project.name}\n\nBekræft at du vil afslutte projekt:\n ${message}`);

      if (conf === true) {
        // Cancel project
        const options = {
          message
        };

        this.isSubmitting = true;

        ProjectService.cancelProject(this.project.id, options, ({ data }) => {
          this.isSubmitting = false;
          this.showModal = false;

          this.$emit('cancelled', data);

          // Reload projects
          this.loadProjects();
        });
      } else {
        // Cancel aborted
        alert('Projektafslutning afbrudt')
      }
    },
    handler(e, row) {
      // if (!this.projectIsOpen(row.status)) {
      //   return;
      // }

      this.lastActionDays = this.calculateDaysSince(new Date(row.updated_at));
      this.creationDays = this.calculateDaysSince(new Date(row.created_at));
      this.currentStatus = row.status;
      this.project = row;

      e.preventDefault();
      this.showContextMenu = true;
      this.openContextMenu(e);
    },
    openContextMenu(e) {
      this.$refs.menu.open(e);
    },
    conversationHandler(e, row, userType) {
      e.preventDefault();

      this.userType = userType;
      this.showConversationContextMenu = true;
      this.conversationId = row.conversation.id;

      // Get user id based on user type
      this.userId = userType === 'customer' ? row.customer.id : row.student.id;

      this.openConversationContextMenu(e);
    },
    openConversationContextMenu(e) {
      this.$refs.cmenu.openMenu(e);
    },
    handleSort(field, order) {
      // store data in session storage
      sessionStorage.setItem('sortField', field);
      sessionStorage.setItem('sortOrder', order);
    },
    showMessageModalForProject(project) {
      this.isShowingMessagesModal = true
      this.conversationForMessagesModal = project
    },

    showNotesModalForProject(project) {
      this.isShowingProjectNotesModal = true;
      this.notesForProject = project;
    },
    updateProjectNotes(e) {
      window.console.log(e);
      this.project.notes = e;
      this.isShowingProjectNotesModal = false;
    },

    hideModal() {
      this.isShowingMessagesModal = false
    },
    hideProjectNotesModal() {
      this.isShowingProjectNotesModal = false
    },

    loadProjects() {
      this.loading = true

      const filters = []

      if (this.filter === 'open') {
        filters.push({
          key: 'status',
          operator: 'in',
          value: [
            'project-created',
            'project-clarification',
            'project-accepted',
            'project-offer-posted',
            'project-offer-accepted'
          ]
        })
      }

      if (this.filter === 'closed') {
        filters.push({
          key: 'status',
          operator: 'eq',
          value: 'project-finalized'
        })
      }

      if (
        [
          'student-closed-project',
          'project-finalized',
          'project-created',
          'project-clarification',
          'project-offer-posted',
          'customer-closed-project',
          'employee-closed-project',
          'project-offer-accepted'
        ].find(element => this.filter === element)
      ) {
        filters.push({
          key: 'status',
          operator: 'eq',
          value: this.filter
        })
      }

      const options = {
        page: this.page,
        limit: this.limit,

        includes: [
          'skill',
          'student',
          'customer',
          'conversation.sender',
          'conversation.addressee'
        ],

        filter_groups: [
          {
            filters
          }
        ]
      }

      ProjectService.findAll(options, ({ data: { rows, total } }) => {
        this.projects = rows
        this.total = total

        this.loading = false
      })
    },

    loadStats() {
      const openProjectsOptions = {
        filter_groups: [
          {
            filters: [
              {
                key: 'status',
                operator: 'in',
                value: [
                  'project-created',
                  'project-accepted',
                  'project-offer-posted',
                  'project-offer-accepted'
                ]
              }
            ]
          }
        ]
      }

      ProjectService.count(openProjectsOptions, ({ data }) => {
        this.stats.openProjects = data.count
      })

      ProjectService.value(openProjectsOptions, ({ data }) => {
        this.stats.projectValue = data.value
      })

      const acceptedProjectsOptions = {
        filter_groups: [
          {
            filters: [
              {
                key: 'status',
                operator: 'eq',
                value: ['project-offer-accepted']
              }
            ]
          }
        ]
      }

      ProjectService.count(acceptedProjectsOptions, ({ data }) => {
        this.stats.acceptedProjects = data.count
      })

      const completedProjectOptions = {
        filter_groups: [
          {
            filters: [
              {
                key: 'status',
                operator: 'eq',
                value: 'project-finalized'
              }
            ]
          }
        ]
      }

      ProjectService.count(completedProjectOptions, ({ data }) => {
        this.stats.completedProjects = data.count
      })
    }
  },
  watch: {
    filter() {
      this.page = 1
      this.loadProjects()
    },

    page() {
      this.loadProjects()
    }
  },

  mounted() {
    this.loadProjects()
    this.loadStats()
  }
}
</script>


<style lang="scss" scoped>
.administration {
  background: #f5f7fa;
}
</style>


// WEBPACK FOOTER //
// src/taskflex/administration/pages/projects/List.vue